import {
  ENTITY_KIND_LABELS,
  ENTITY_KINDS,
  type EntityKind,
  RELATION_CATEGORIES,
  type RelationCategory,
  type RelationTypeDef,
} from '@hoopoe/shared';
import { createFileRoute } from '@tanstack/react-router';
import { Archive, Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import { Tip } from '../components/guidance/tip';
import { PageHeader } from '../components/layout/app-shell';
import { CheckRow, EditorError, Panel, SelectRow, TextRow } from '../components/schema/editor-kit';
import { Button } from '../components/ui/button';
import { useSchema, useSchemaEdit } from '../lib/schema';

export const Route = createFileRoute('/admin/relations')({ component: RelationsPage });

const CATEGORY_LABELS: Record<RelationCategory, { title: string; help: string }> = {
  relation: { title: 'Relations between actors', help: 'owner of, employer of, member of, adviser to…' },
  participation: {
    title: 'Participation roles',
    help: 'How an actor takes part in an event: speaker, organizer, rapporteur, and vote roles.',
  },
  location: { title: 'Location links', help: 'held at, based in, located in.' },
  flow: { title: 'Money flows', help: 'donated to, paid, invested in: they carry an amount.' },
};

function RelationsPage() {
  const { data: snap } = useSchema();
  const [editing, setEditing] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const edit = useSchemaEdit();
  if (!snap) return <PageHeader title="Relation types" description="Loading…" />;
  return (
    <>
      <PageHeader title="Relation types" description="The links claims can make between entities, shown in both directions." />
      <Tip id="admin-relations" learnMore="schema-and-templates">
        Each type reads both ways (“employer of” / “employed by”). Autocomplete only offers the types valid between the two entity kinds.
      </Tip>
      <div className="mt-4 space-y-6">
        <CheckRow label="Show archived" checked={showArchived} onChange={setShowArchived} />
        {RELATION_CATEGORIES.map(cat => {
          const rows = snap.relationTypes.filter(r => r.category === cat && (showArchived || !r.archived));
          return (
            <Panel key={cat} title={CATEGORY_LABELS[cat].title}>
              <p className="text-sm text-muted-foreground">{CATEGORY_LABELS[cat].help}</p>
              <ul className="divide-y rounded-md border" aria-label={CATEGORY_LABELS[cat].title}>
                {rows.map(r => (
                  <li key={r.id} className="p-2" aria-label={r.label}>
                    {editing === r.id ? (
                      <RelationForm relation={r} onDone={() => setEditing(null)} />
                    ) : (
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className={`font-medium ${r.archived ? 'text-muted-foreground line-through' : ''}`}>{r.label}</span>
                        <span className="text-muted-foreground">
                          {r.symmetric ? '(both ways)' : r.inverseLabel ? `/ ${r.inverseLabel}` : ''}
                        </span>
                        {r.groupKey && <span className="rounded bg-muted px-1.5 text-xs">{r.groupKey}</span>}
                        <span className="text-xs text-muted-foreground">
                          {r.sourceKinds.map(k => ENTITY_KIND_LABELS[k]).join(', ')} →{' '}
                          {r.targetKinds.map(k => ENTITY_KIND_LABELS[k]).join(', ')}
                          {r.hasAmount && ' · amount'}
                          {r.hasPeriod && ' · period'}
                        </span>
                        <span className="ms-auto flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => setEditing(r.id)}>
                            <Pencil aria-hidden="true" /> Edit<span className="sr-only"> {r.label}</span>
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              edit.mutate({
                                path: `/admin/schema/relation-types/${r.id}`,
                                method: 'PATCH',
                                body: { archived: !r.archived },
                              })
                            }
                          >
                            <Archive aria-hidden="true" /> {r.archived ? 'Restore' : 'Archive'}
                            <span className="sr-only"> {r.label}</span>
                          </Button>
                        </span>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
              {editing === `new:${cat}` ? (
                <RelationForm category={cat} onDone={() => setEditing(null)} />
              ) : (
                <Button size="sm" variant="outline" onClick={() => setEditing(`new:${cat}`)}>
                  <Plus aria-hidden="true" /> Add to {CATEGORY_LABELS[cat].title.toLowerCase()}
                </Button>
              )}
            </Panel>
          );
        })}
        <EditorError error={edit.error} />
      </div>
    </>
  );
}

function KindsPicker({ label, value, onChange }: { label: string; value: EntityKind[]; onChange: (v: EntityKind[]) => void }) {
  return (
    <fieldset className="space-y-1">
      <legend className="text-sm font-medium">{label}</legend>
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {ENTITY_KINDS.map(k => (
          <label key={k} className="flex items-center gap-1 text-sm">
            <input
              type="checkbox"
              checked={value.includes(k)}
              onChange={e => onChange(e.target.checked ? [...value, k] : value.filter(x => x !== k))}
            />
            {ENTITY_KIND_LABELS[k]}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function RelationForm({ relation, category, onDone }: { relation?: RelationTypeDef; category?: RelationCategory; onDone: () => void }) {
  const edit = useSchemaEdit();
  const cat = relation?.category ?? category ?? 'relation';
  const [r, setR] = useState({
    label: relation?.label ?? '',
    inverseLabel: relation?.inverseLabel ?? '',
    category: cat,
    groupKey: relation?.groupKey ?? '',
    symmetric: relation?.symmetric ?? false,
    sourceKinds: relation?.sourceKinds ?? (['actor'] as EntityKind[]),
    targetKinds:
      relation?.targetKinds ?? ((cat === 'participation' ? ['event'] : cat === 'location' ? ['location'] : ['actor']) as EntityKind[]),
    hasAmount: relation?.hasAmount ?? cat === 'flow',
    hasPeriod: relation?.hasPeriod ?? cat !== 'participation',
    description: relation?.description ?? '',
  });
  const set = (p: Partial<typeof r>) => setR(prev => ({ ...prev, ...p }));
  return (
    <form
      className="space-y-3 rounded-md bg-background p-2"
      aria-label={relation ? `Edit ${relation.label}` : 'New relation type'}
      onSubmit={e => {
        e.preventDefault();
        const body = { ...r, inverseLabel: r.symmetric ? null : r.inverseLabel || null, groupKey: r.groupKey || null };
        edit.mutate(
          relation
            ? { path: `/admin/schema/relation-types/${relation.id}`, method: 'PATCH', body }
            : { path: '/admin/schema/relation-types', body },
          { onSuccess: onDone },
        );
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <TextRow label="Label (reads from → to)" required value={r.label} onChange={v => set({ label: v })} placeholder="e.g. adviser to" />
        {!r.symmetric && (
          <TextRow
            label="Inverse label (reads to → from)"
            value={r.inverseLabel}
            onChange={v => set({ inverseLabel: v })}
            placeholder="e.g. advised by"
          />
        )}
        <SelectRow<RelationCategory>
          label="Category"
          value={r.category}
          options={RELATION_CATEGORIES.map(c => ({ value: c, label: CATEGORY_LABELS[c].title }))}
          onChange={v => set({ category: v })}
        />
        <TextRow
          label="Sub-group"
          value={r.groupKey}
          onChange={v => set({ groupKey: v })}
          placeholder="e.g. vote"
          help="Groups roles together, e.g. the vote roles for / against / abstain / absent."
        />
        <TextRow label="Description" value={r.description} onChange={v => set({ description: v })} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <KindsPicker label="From" value={r.sourceKinds} onChange={v => set({ sourceKinds: v })} />
        <KindsPicker label="To" value={r.targetKinds} onChange={v => set({ targetKinds: v })} />
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        <CheckRow label="Same both ways (symmetric)" checked={r.symmetric} onChange={v => set({ symmetric: v })} />
        <CheckRow label="Has an amount" checked={r.hasAmount} onChange={v => set({ hasAmount: v })} />
        <CheckRow label="Has a period" checked={r.hasPeriod} onChange={v => set({ hasPeriod: v })} />
      </div>
      <EditorError error={edit.error} />
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={!r.label.trim() || edit.isPending}>
          {relation ? 'Save' : 'Add relation type'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
